import { useArcConnections } from "./ArcConnectionsContext";
import { useMemo, useState } from "react";
import { useUndoRedoKeyboard } from "../../hooks/useUndoRedoKeyboard";
import { clientToSvgUserSpace } from "../../utils/svgViewBoxPointer";

export function ArcConnectionsViewer() {
    const {
        nodes,
        connections,
        bezierAngle,
        bezierAngleRandomness,
        angleFlipPercent,
        nodeBaseSize,
        nodeSizeIncrement,
        nodeSizeRandomness,
        nodeColor,
        strokeColor,
        connectionOpacity,
        viewBoxAttr,
        addNode,
        brushSize,
        undo,
        redo,
    } = useArcConnections();

    useUndoRedoKeyboard({ undo, redo });

    const [brushHover, setBrushHover] = useState<{
        x: number;
        y: number;
    } | null>(null);

    const nodeConnectionCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        nodes.forEach((node) => {
            counts[node.id] = 0;
        });
        connections.forEach((conn) => {
            counts[conn.from] = (counts[conn.from] || 0) + 1;
            counts[conn.to] = (counts[conn.to] || 0) + 1;
        });
        return counts;
    }, [nodes, connections]);

    const handleSvgClick = (
        event: React.MouseEvent<SVGSVGElement, MouseEvent>
    ) => {
        const pt = clientToSvgUserSpace(
            event.currentTarget,
            event.clientX,
            event.clientY,
            viewBoxAttr,
        );
        if (!pt) return;
        addNode(pt.x, pt.y);
    };

    const handleSvgPointerMove = (
        event: React.MouseEvent<SVGSVGElement, MouseEvent>
    ) => {
        const pt = clientToSvgUserSpace(
            event.currentTarget,
            event.clientX,
            event.clientY,
            viewBoxAttr,
        );
        setBrushHover(pt);
    };

    const handleSvgPointerLeave = () => {
        setBrushHover(null);
    };

    // Deterministic hash function to decide if a connection should be flipped
    const shouldFlipConnection = (fromId: string, toId: string): boolean => {
        // Create a deterministic hash from the connection IDs
        const hash = (fromId + toId).split("").reduce((acc, char) => {
            return ((acc << 5) - acc + char.charCodeAt(0)) | 0;
        }, 0);
        // Use absolute value and modulo to get a value between 0-99
        const hashValue = Math.abs(hash) % 100;
        return hashValue < angleFlipPercent;
    };

    const getBezierPath = (
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        fromId: string,
        toId: string
    ): string => {
        const dx = x2 - x1;
        const dy = y2 - y1;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const lineAngle = Math.atan2(dy, dx);

        // Apply randomness to bezier angle
        let effectiveAngle = bezierAngle;
        if (bezierAngleRandomness > 0) {
            const hash = (fromId + toId).split("").reduce((acc, char) => {
                return ((acc << 5) - acc + char.charCodeAt(0)) | 0;
            }, 0);
            const randomValue = (Math.abs(hash) % 2000) / 1000 - 1; // -1 to 1
            const angleVariation = bezierAngle * bezierAngleRandomness * randomValue;
            effectiveAngle = Math.max(0, Math.min(90, bezierAngle + angleVariation));
        }

        const angleRad = (effectiveAngle * Math.PI) / 180;
        const flip = shouldFlipConnection(fromId, toId);

        // Control point distance from nodes
        const controlDistance = distance * 0.4;

        // Control points are positioned at the bezier angle relative to the line
        // Angle of 0 = straight line, higher angles = more curved
        // If flipped, reverse the angle direction
        const cp1Angle = flip
            ? lineAngle - angleRad
            : lineAngle + angleRad;
        const cp2Angle = flip
            ? lineAngle + angleRad + Math.PI
            : lineAngle - angleRad + Math.PI; // Opposite direction from node 2

        const cp1x = x1 + Math.cos(cp1Angle) * controlDistance;
        const cp1y = y1 + Math.sin(cp1Angle) * controlDistance;
        const cp2x = x2 + Math.cos(cp2Angle) * controlDistance;
        const cp2y = y2 + Math.sin(cp2Angle) * controlDistance;

        return `M ${x1} ${y1} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x2} ${y2}`;
    };

    const getNodeSize = (nodeId: string): number => {
        const connectionCount = nodeConnectionCounts[nodeId] || 0;
        const baseSize = nodeBaseSize + connectionCount * nodeSizeIncrement;

        if (nodeSizeRandomness <= 0) {
            return baseSize;
        }

        // Deterministic hash function to get a consistent random value for each node
        const hash = nodeId.split("").reduce((acc, char) => {
            return ((acc << 5) - acc + char.charCodeAt(0)) | 0;
        }, 0);
        // Convert hash to a value between -1 and 1
        const randomValue = (Math.abs(hash) % 2000) / 1000 - 1;
        // Apply randomness as a percentage of the base size
        const randomOffset = baseSize * nodeSizeRandomness * randomValue;

        return Math.max(1, baseSize + randomOffset);
    };

    return (
        <div className="panel-viewer arc-connections-viewer">
            <div className="path-tool-preview">
                <div className="svg-scroll-wrapper">
                    <svg
                        viewBox={viewBoxAttr}
                        xmlns="http://www.w3.org/2000/svg"
                        className="path-tool-svg"
                        onClick={handleSvgClick}
                        onPointerMove={handleSvgPointerMove}
                        onPointerLeave={handleSvgPointerLeave}
                        style={{ cursor: "crosshair" }}
                    >
                        <g className="connections">
                            {connections.map((conn, idx) => {
                                const fromNode = nodes.find(
                                    (n) => n.id === conn.from
                                );
                                const toNode = nodes.find(
                                    (n) => n.id === conn.to
                                );
                                if (!fromNode || !toNode) return null;

                                return (
                                    <path
                                        key={`${conn.from}-${conn.to}-${idx}`}
                                        d={getBezierPath(
                                            fromNode.x,
                                            fromNode.y,
                                            toNode.x,
                                            toNode.y,
                                            conn.from,
                                            conn.to
                                        )}
                                        stroke={strokeColor}
                                        strokeWidth="2"
                                        fill="none"
                                        opacity={connectionOpacity}
                                    />
                                );
                            })}
                        </g>

                        <g className="nodes">
                            {nodes.map((node) => (
                                <circle
                                    key={node.id}
                                    cx={node.x}
                                    cy={node.y}
                                    r={getNodeSize(node.id)}
                                    fill={nodeColor}
                                    stroke={strokeColor}
                                    strokeWidth="2"
                                />
                            ))}
                        </g>
                        {brushHover && brushSize > 0 && (
                            <circle
                                className="svg-brush-hover-preview"
                                cx={brushHover.x}
                                cy={brushHover.y}
                                r={brushSize}
                                stroke={strokeColor}
                            />
                        )}
                    </svg>
                </div>
            </div>
        </div>
    );
}

